import { Controller } from 'stimulus';

// The Schedule screen (/schedule): one timeline grouped by people, equipment,
// or job, edited by dragging.
//
// The server renders the grid (schedules/_board) with a JSON model of the
// window, the tasks, the people's working days and time off, and the
// attention cases. This controller uses that model for what has to feel
// instant (the drag itself, the ghost preview of later tasks, snapping to
// working days, the chips' spotlight) and sends every real change to the
// server, which applies the same rules authoritatively and answers with
// what changed, any overlap it caused, and an undo list. After each change
// the board is re-fetched whole and swapped in.
//
// Drags: a task bar moves (or its right edge resizes it); dropped on another
// person's or machine's section it is handed over; a job's bracket moves
// every open task of the job; a block (time off, hold, downtime) moves or
// resizes, and a click on one opens the popover; a drag-out on the empty
// part of a resource row creates a block through the popover.
const DRAG_MAX_DAYS = 400;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const OPEN = ['Not Started', 'In Process', 'On Hold'];
const KIND_LABEL = { timeoff: 'Time off', hold: 'Hold', down: 'Planned downtime' };
const SEVERITY = { late: 'red', pastDue: 'red', machine: 'red', calDue: 'red', people: 'amber', unassigned: 'amber', samples: 'amber' };

export default class extends Controller {
  static targets = ['board', 'grid', 'chips', 'chipsSource', 'note', 'pushSwitch',
    'pop', 'popTitle', 'popDates', 'popKind', 'popText', 'popDelete', 'top', 'topSource', 'range', 'assign'];

  connect() {
    this.canEdit = this.data.get('canEdit') === 'true';
    this.userId = this.data.get('userId');
    this.group = this.data.get('group');
    this.push = true;
    this.spot = { active: {}, idx: {}, scroll: false };
    this.lastUndo = null;
    this.drag = null;
    this.create = null;
    this.popState = null;
    this.popOpenedAt = 0;
    this.expanded = new Set(this.parseJson(this.data.get('expanded'), []));
    this.collapsed = new Set(this.parseJson(this.data.get('collapsed'), []));

    this._down = (e) => this.onPointerDown(e);
    this._move = (e) => this.onPointerMove(e);
    this._up = (e) => this.onPointerUp(e, false);
    this._cancel = (e) => this.onPointerUp(e, true);
    this._dbl = (e) => this.onDoubleClick(e);
    this._click = (e) => this.onClick(e);
    this._key = (e) => this.onKey(e);
    this._docClick = (e) => this.onDocumentClick(e);
    // Back / Forward between windows: Turbolinks restores the entries this
    // controller pushes (see pushUrl); without Turbolinks the grid is swapped
    this._popstate = () => this.go(window.location.href, null, false);
    this.navSeq = 0;
    this.navigating = false;
    this.element.addEventListener('pointerdown', this._down);
    this.element.addEventListener('pointermove', this._move);
    this.element.addEventListener('pointerup', this._up);
    this.element.addEventListener('pointercancel', this._cancel);
    this.element.addEventListener('dblclick', this._dbl);
    this.element.addEventListener('click', this._click);
    document.addEventListener('keydown', this._key);
    document.addEventListener('click', this._docClick);
    // The shared modal (comments, the task form) closes itself after a save:
    // refresh, so a new comment or a changed task shows at once
    this._modalClosed = () => this.refresh();
    document.addEventListener('modal:close', this._modalClosed);
    if (!this.turbolinks) window.addEventListener('popstate', this._popstate);
    this.readBoard();
  }

  disconnect() {
    document.removeEventListener('keydown', this._key);
    document.removeEventListener('click', this._docClick);
    document.removeEventListener('modal:close', this._modalClosed);
    window.removeEventListener('popstate', this._popstate);
  }

  get turbolinks() {
    const T = window.Turbolinks;
    return T && T.supported && T.controller ? T : null;
  }

  // A history entry Turbolinks recognizes, so Back from the window (or from a
  // page opened off it) is a normal Turbolinks restore of that URL
  pushUrl(href) {
    const T = this.turbolinks;
    if (T) T.controller.pushHistoryWithLocationAndRestorationIdentifier(href, T.uuid());
    else window.history.pushState({ schedule: true }, '', href);
  }

  // ----- moving the window or the grouping in place -----

  // The top bar's links (Today, the arrows, Day / Week, Group by) fetch only
  // the grid and swap it in, with the new range shown and the old grid
  // dimmed meanwhile. A modified click (new tab) is left to the browser.
  navigate(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const link = event.currentTarget;
    this.go(link.href, link.dataset.label, true, link.dataset.from);
  }

  // While a window loads, the top bar still belongs to the old one. Point the
  // arrows (and Group by) at the window being loaded, so a second quick click
  // on › goes a step further instead of to the same window again.
  retarget(url, from) {
    if (!from || !this.hasTopTarget) return;
    const bar = this.topTarget.querySelector('[data-zooms]');
    const zooms = bar ? this.parseJson(bar.dataset.zooms, {}) : {};
    const zoom = url.searchParams.get('zoom') || 'day';
    const z = zooms[zoom];
    if (!z) return;
    const start = this.utcDate(from);
    const group = url.searchParams.get('group');
    const point = (a, day) => {
      const u = new URL(a.href, window.location.href);
      u.searchParams.set('from', this.utcIso(day));
      u.searchParams.set('zoom', zoom);
      if (group && a.classList.contains('sch-arrow')) u.searchParams.set('group', group);
      a.href = `${u.pathname}${u.search}`;
      a.dataset.from = this.utcIso(day);
      a.dataset.label = this.utcRange(day, z.days);
    };
    this.topTarget.querySelectorAll('a.sch-arrow').forEach((a) => {
      point(a, this.utcAdd(start, (a.title === 'Earlier' ? -1 : 1) * z.step));
    });
    this.topTarget.querySelectorAll('.sch-seg a.sch-seg-btn').forEach((a) => {
      if (new URL(a.href, window.location.href).searchParams.has('from')) point(a, start); // Group by (zoom links open today)
    });
  }

  // Dates for retarget, in UTC so daylight saving never shifts a day
  utcDate(iso) { const p = iso.split('-').map(Number); return new Date(Date.UTC(p[0], p[1] - 1, p[2])); }
  utcAdd(d, days) { return new Date(d.getTime() + days * 86400000); }
  utcIso(d) { return d.toISOString().slice(0, 10); }
  utcRange(start, days) {
    const end = this.utcAdd(start, days - 1);
    return `${MONTHS[start.getUTCMonth()]} ${start.getUTCDate()} – ${MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }

  go(href, label, push, from) {
    const url = new URL(href, window.location.href);
    const seq = ++this.navSeq;
    this.retarget(url, from || url.searchParams.get('from'));
    const sameGroup = (url.searchParams.get('group') || this.group) === this.group;
    // The note to put back afterwards: the one from before the first of
    // several quick clicks, not another click's "Loading…"
    if (!this.navigating && this.hasNoteTarget) this.noteBefore = this.noteTarget.innerHTML;
    this.navigating = true;
    this.closePop();
    this.boardTarget.classList.add('sch-loading');
    if (label && this.hasRangeTarget) this.rangeTarget.textContent = label;
    if (label) this.note(`Loading ${this.escape(label)}…`);
    const loadingNote = this.hasNoteTarget ? this.noteTarget.innerHTML : '';
    const rowsUrl = `${this.data.get('rowsBase')}${url.search}`;
    const sep = url.search ? '&' : '?';
    fetch(`${rowsUrl}${sep}nav=1`, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then((html) => {
        if (seq !== this.navSeq) return; // a later click won
        const box = this.boardTarget.querySelector('.sch-scroller');
        const top = box && sameGroup ? box.scrollTop : 0;
        this.boardTarget.innerHTML = html;
        if (this.hasTopSourceTarget && this.hasTopTarget) {
          this.topTarget.innerHTML = this.topSourceTarget.innerHTML;
          const bar = this.topTarget.querySelector('[data-group]');
          if (bar) this.group = bar.dataset.group;
        }
        this.data.set('rowsUrl', rowsUrl);
        if (push) this.pushUrl(url.href);
        this.readBoard();
        const fresh = this.boardTarget.querySelector('.sch-scroller');
        if (fresh) fresh.scrollTop = top;
        this.boardTarget.classList.remove('sch-loading');
        this.navigating = false;
        // Put the old note back unless something (a save) has written its own
        if (this.hasNoteTarget && this.noteTarget.innerHTML === loadingNote) this.noteTarget.innerHTML = this.noteBefore || '';
        // An edit saved while this loaded: its refresh waited (refresh)
        if (this.refreshAfterNav) { this.refreshAfterNav = false; this.refresh(); }
      })
      .catch(() => { if (seq === this.navSeq) window.location.assign(url.href); });
  }

  // ----- the board and its model -----

  readBoard() {
    const grid = this.boardTarget.querySelector('.sch-grid');
    this.model = grid ? this.parseJson(grid.dataset.model, null) : null;
    if (!this.model) return;
    const parts = this.model.start.split('-').map(Number);
    this.startDate = new Date(parts[0], parts[1] - 1, parts[2]);
    this.tasksById = {};
    this.model.tasks.forEach((t) => { this.tasksById[t.id] = t; });
    this.blocksById = {};
    this.model.blocks.forEach((b) => { this.blocksById[`${b.kind}:${b.id}`] = b; });
    this.closeAssign(); // its anchor row was replaced
    this.renderChips();
    this.applySpotlight();
  }

  get grid() { return this.boardTarget.querySelector('.sch-grid'); }

  parseJson(text, fallback) {
    try { return text ? JSON.parse(text) : fallback; } catch (e) { return fallback; }
  }

  dateOf(i) { const d = new Date(this.startDate); d.setDate(d.getDate() + i); return d; }
  dow(i) { return this.dateOf(i).getDay(); }
  fmt(i) { const d = this.dateOf(i); return `${MONTHS[d.getMonth()]} ${d.getDate()}`; }
  iso(i) {
    const d = this.dateOf(i);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }
  range(a, b) { return a === b ? this.fmt(a) : `${this.fmt(a)} – ${this.fmt(b)}`; }
  pct(i) { return `${((i / this.model.days) * 100).toFixed(3)}%`; }
  pctW(n) { return `${((n / this.model.days) * 100).toFixed(3)}%`; }

  // ----- calendar rules (mirror of Scheduling::Calendar) -----

  personWorks(personId, i) {
    const p = this.model.people[personId];
    if (!p) return this.dow(i) !== 0 && this.dow(i) !== 6;
    if (p.days.indexOf(this.dow(i)) < 0) return false;
    if (this.model.closed.indexOf(i) >= 0) return false;
    return !p.off.some((r) => i >= r[0] && i <= r[1]);
  }

  snapStart(task, i) {
    let guard = 0;
    if (task.users.length) {
      while (guard++ < 21 && !task.users.every((id) => this.personWorks(id, i))) i += 1;
    } else if (!task.equipment.length) {
      while (guard++ < 21 && (this.dow(i) === 0 || this.dow(i) === 6 || this.model.closed.indexOf(i) >= 0)) i += 1;
    }
    return i;
  }

  shiftTask(task, delta) {
    const s = this.snapStart(task, task.s + delta);
    return { s, e: s + (task.e - task.s) };
  }

  laterTasks(task) {
    return this.model.tasks.filter((x) => x.job === task.job && x.order > task.order && x.typed && OPEN.indexOf(x.status) >= 0);
  }

  // ----- expand / collapse -----

  // A job row, a bucket row (Late, Earlier, Later) holding job rows, or a
  // person's or machine's row holding their jobs
  toggleJob(event) {
    const row = event.currentTarget.closest('.sch-job, .sch-bkt, .sch-res');
    if (!row) return;
    const key = row.dataset.key;
    const open = !row.classList.contains('open');
    this.setRowOpen(row, open);
    if (row.dataset.defaultOpen === 'true') {
      if (open) this.collapsed.delete(key); else this.collapsed.add(key);
    } else if (open) this.expanded.add(key); else this.expanded.delete(key);
    this.savePrefs();
  }

  setRowOpen(row, open) {
    row.classList.toggle('open', open);
    if (row.classList.contains('sch-bkt') || row.classList.contains('sch-res')) {
      const attr = row.classList.contains('sch-res') ? 'data-res-body' : 'data-bucket-body';
      const body = this.grid.querySelector(`[${attr}="${row.dataset.key}"]`);
      if (body) body.hidden = !open;
      return;
    }
    this.grid.querySelectorAll(`.sch-task[data-parent="${row.dataset.key}"]`).forEach((r) => { r.hidden = !open; });
  }

  stop(event) { event.stopPropagation(); }

  savePrefs(extra = {}) {
    fetch(this.data.get('prefsUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({ expanded: Array.from(this.expanded), collapsed: Array.from(this.collapsed), ...extra })
    }).catch(() => {});
  }

  // Collapse all / Expand all: every person's or machine's section folds to
  // its own row, or opens. Each section's own choice is dropped, so the
  // screen starts clean from the new default.
  collapseAll(event) {
    const collapse = event.currentTarget.dataset.all === 'collapse';
    [this.expanded, this.collapsed].forEach((set) => {
      Array.from(set).forEach((key) => { if (key.indexOf('res:') === 0) set.delete(key); });
    });
    this.grid.querySelectorAll('.sch-res[data-key]').forEach((row) => {
      row.dataset.defaultOpen = String(!collapse);
      this.setRowOpen(row, !collapse);
    });
    event.currentTarget.parentElement.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b === event.currentTarget));
    this.savePrefs({ collapse_all: collapse });
  }

  // ----- assigning: the picker and Give to -----

  // Open, unassigned tasks of a job in the model
  unassignedIn(job) {
    return this.model.tasks.filter((t) => String(t.job) === String(job) && !t.users.length && OPEN.indexOf(t.status) >= 0).map((t) => t.id);
  }

  // Give to: the job's obvious person on all its unassigned tasks
  giveTo(event) {
    event.stopPropagation();
    const ids = this.unassignedIn(event.currentTarget.dataset.jobId);
    if (ids.length) this.send(this.data.get('assignUrl'), 'POST', { task_ids: ids, kind: 'person', to_id: event.currentTarget.dataset.userId });
  }

  // Needs a person on a task row
  openAssign(event) {
    event.stopPropagation();
    const task = this.tasksById[event.currentTarget.dataset.taskId];
    if (task) this.openPicker({ job: task.job, taskId: task.id, scope: 'task' }, event.currentTarget);
  }

  // Assign… on an Unassigned job row
  openAssignJob(event) {
    event.stopPropagation();
    this.openPicker({ job: event.currentTarget.dataset.jobId, taskId: null, scope: 'job' }, event.currentTarget);
  }

  openPicker(state, anchor) {
    if (!this.hasAssignTarget) return;
    this.closePop();
    this.pick = { tab: 'people', query: '', ...state };
    this.pickAnchor = anchor;
    this.drawPicker();
    this.placePicker();
    const search = this.assignTarget.querySelector('input[type="search"]');
    if (search) search.focus({ preventScroll: true });
    if (!this._follow) {
      // follows the page and the grid as they scroll, but not its own list
      // (re-placing it then would snap the list back to the top)
      this._follow = (e) => {
        if (!this.pick || (e && e.target && e.target.nodeType === 1 && this.assignTarget.contains(e.target))) return;
        this.placePicker();
      };
      window.addEventListener('scroll', this._follow, true);
      window.addEventListener('resize', this._follow);
    }
  }

  closeAssign() {
    this.pick = null;
    this.pickAnchor = null;
    if (this.hasAssignTarget) this.assignTarget.hidden = true;
    if (this._follow) {
      window.removeEventListener('scroll', this._follow, true);
      window.removeEventListener('resize', this._follow);
      this._follow = null;
    }
  }

  pickIds() {
    return this.pick.scope === 'task' && this.pick.taskId ? [this.pick.taskId] : this.unassignedIn(this.pick.job);
  }

  // The days of the tasks being assigned, each task on its own: [[s, e], ...]
  // (for a whole job, the gaps between its tasks don't count)
  pickRanges(ids) {
    return ids.map((id) => [this.tasksById[id].s, this.tasksById[id].e]);
  }

  // How busy a person is on those days, not counting the tasks being
  // assigned: off and non-working days first, then other open tasks and
  // holds. rank orders the list (lower first).
  personStatus(id, ranges, skip) {
    const p = this.model.people[id];
    const meets = (s, e) => ranges.some((r) => s <= r[1] && e >= r[0]);
    const off = [];
    const notWorked = [];
    ranges.forEach(([s, e]) => {
      for (let i = s; i <= Math.min(e, s + 60); i += 1) {
        if (p.off.some((r) => i >= r[0] && i <= r[1])) off.push(i);
        const d = this.dow(i);
        if (d !== 0 && d !== 6 && p.days.indexOf(d) < 0 && notWorked.indexOf(DAY_NAMES[d]) < 0) notWorked.push(DAY_NAMES[d]);
      }
    });
    off.sort((a, b) => a - b);
    if (off.length) return { cls: 'no', text: `Off ${this.range(off[0], off[off.length - 1])}`, rank: 3 };
    if (notWorked.length) return { cls: 'no', text: `Doesn't work ${notWorked.join(', ')}`, rank: 3 };
    const tasks = this.model.tasks.filter((t) => t.users.indexOf(Number(id)) >= 0 && OPEN.indexOf(t.status) >= 0
      && skip.indexOf(t.id) < 0 && meets(t.s, t.e));
    const holds = this.model.blocks.filter((b) => b.kind === 'hold' && String(b.user) === String(id) && meets(b.s, b.e));
    const n = tasks.length + holds.length;
    if (!n) return { cls: 'free', text: 'Free', rank: 0, n };
    const title = tasks.map((t) => `${t.name} (${(this.model.jobs[t.job] || {}).label || ''}) ${this.range(t.s, t.e)}`)
      .concat(holds.map((b) => `Hold: ${b.label} ${this.range(b.s, b.e)}`)).join('\n');
    return { cls: 'busy', text: `${n} other ${n === 1 ? 'task' : 'tasks'}`, rank: 1, n, title };
  }

  machineStatus(id, ranges, skip) {
    const m = this.model.machines[id];
    const last = Math.max(...ranges.map((r) => r[1]));
    const meets = (s, e) => ranges.some((r) => s <= r[1] && e >= r[0]);
    if (m.status && (m.since === null || m.since <= last)) return { cls: 'no', text: m.since === null ? m.status : `${m.status} since ${this.fmt(m.since)}`, rank: 3 };
    const tasks = this.model.tasks.filter((t) => t.equipment.indexOf(Number(id)) >= 0 && OPEN.indexOf(t.status) >= 0
      && skip.indexOf(t.id) < 0 && meets(t.s, t.e));
    const cal = m.cal !== null && m.cal < last;
    if (tasks.length) return { cls: 'busy', text: `Booked: ${tasks.map((t) => t.name).join(', ')}`, rank: cal ? 2 : 1 };
    if (cal) return { cls: 'no', text: `Calibration due ${this.fmt(m.cal)}`, rank: 2 };
    return { cls: 'free', text: 'Free', rank: 0 };
  }

  drawPicker() {
    const pk = this.pick;
    const ids = this.pickIds();
    if (!ids.length) { this.closeAssign(); return; }
    const job = this.model.jobs[pk.job] || { label: '' };
    const tasks = ids.map((id) => this.tasksById[id]);
    const s = Math.min(...tasks.map((t) => t.s));
    const e = Math.max(...tasks.map((t) => t.e));
    const all = this.unassignedIn(pk.job);
    const title = pk.scope === 'task' && pk.taskId ? this.tasksById[pk.taskId].name : `${all.length} unassigned ${all.length === 1 ? 'task' : 'tasks'}`;
    const scope = pk.taskId && all.length > 1 ? `
      <div class="sch-pick-scope">
        <label><input type="radio" name="sch-pick-scope" value="task" data-action="change->schedule#pickScope"${pk.scope === 'task' ? ' checked' : ''}> This task</label>
        <label><input type="radio" name="sch-pick-scope" value="job" data-action="change->schedule#pickScope"${pk.scope === 'job' ? ' checked' : ''}> All ${all.length} unassigned tasks in this job</label>
      </div>` : '';
    const tab = (key, label) => `<button type="button" class="sch-seg-btn${pk.tab === key ? ' on' : ''}" data-action="click->schedule#pickTab" data-tab="${key}">${label}</button>`;
    this.assignTarget.innerHTML = `
      <div class="sch-pick-head">
        <div class="sch-pick-title"><span>Assign ${this.escape(title)}</span><button type="button" class="sch-pick-x" data-action="click->schedule#closeAssign" aria-label="Close">&times;</button></div>
        <div class="sch-pop-dates">${this.range(s, e)} · ${this.escape(job.label)}</div>
        <span class="sch-seg">${tab('people', 'People')}${tab('machines', 'Equipment')}</span>
        ${scope}
        <input type="search" class="sch-pop-text" placeholder="Search ${pk.tab === 'people' ? 'people' : 'equipment'}" value="${this.escape(pk.query)}"
               data-action="input->schedule#pickSearch" autocomplete="off">
      </div>
      <div class="sch-pick-list">${this.pickList(ids)}</div>
      <div class="sch-pick-foot">${pk.tab === 'people' ? 'Busy counts the other open tasks and holds on these days; point at it to see them.' : 'A machine takes the place of the task\'s machine; the person stays.'}</div>`;
    this.assignTarget.hidden = false;
  }

  // The picker's rows: people on the job first, then everyone else, each
  // group least busy first (inactive people are left out); or the equipment
  pickList(ids) {
    const pk = this.pick;
    const q = pk.query.trim().toLowerCase();
    const ranges = this.pickRanges(ids);
    const row = (kind, id, name, avatar, st, why) => `
      <button type="button" class="sch-who${st.rank >= 3 ? ' dim' : ''}" data-action="click->schedule#pickResource" data-kind="${kind}" data-id="${id}"${st.title ? ` title="${this.escape(st.title)}"` : ''}>
        ${avatar}<span class="sch-who-name">${this.escape(name)}${why ? `<small>${this.escape(why)}</small>` : ''}</span><span class="sch-who-st ${st.cls}">${this.escape(st.text)}</span>
      </button>`;
    if (pk.tab === 'machines') {
      const machines = this.model.machines || {};
      const rows = Object.keys(machines)
        .filter((id) => !q || machines[id].name.toLowerCase().indexOf(q) >= 0)
        .map((id) => ({ id, st: this.machineStatus(id, ranges, ids) }))
        .sort((a, b) => a.st.rank - b.st.rank || machines[a.id].name.localeCompare(machines[b.id].name));
      if (!rows.length) return `<div class="sch-pick-group">${q ? 'No equipment matches' : 'No equipment on the schedule'}</div>`;
      return `<div class="sch-pick-group">Equipment, free first</div>${rows.map((r) => row('machine', r.id, machines[r.id].name,
        '<span class="sch-av sch-av-machine">&#9881;</span>', r.st, '')).join('')}`;
    }
    const people = this.model.people;
    const usable = (id) => people[id] && people[id].active && (!q || people[id].name.toLowerCase().indexOf(q) >= 0);
    const avatar = (id) => `<span class="sch-av ${people[id].tone || ''}">${this.escape(people[id].initials)}</span>`;
    const job = this.model.jobs[pk.job] || { assignees: [] };
    const jobTasks = this.model.tasks.filter((t) => String(t.job) === String(pk.job));
    const onJob = [];
    (job.assignees || []).concat(...jobTasks.map((t) => t.users)).forEach((id) => {
      if (people[id] && onJob.indexOf(String(id)) < 0) onJob.push(String(id));
    });
    const why = (id) => ((job.assignees || []).map(String).indexOf(id) >= 0 ? 'Job assignee'
      : `On ${jobTasks.filter((t) => t.users.indexOf(Number(id)) >= 0).map((t) => t.name).join(', ')}`);
    const ranked = (list) => list.map((id) => ({ id, st: this.personStatus(id, ranges, ids) }))
      .sort((a, b) => a.st.rank - b.st.rank || (a.st.n || 0) - (b.st.n || 0) || people[a.id].name.localeCompare(people[b.id].name));
    const on = ranked(onJob.filter(usable));
    const rest = ranked(Object.keys(people).filter((id) => onJob.indexOf(id) < 0 && usable(id)));
    let html = '';
    if (on.length) html += `<div class="sch-pick-group">On this job</div>${on.map((r) => row('person', r.id, people[r.id].name, avatar(r.id), r.st, why(r.id))).join('')}`;
    if (rest.length) html += `<div class="sch-pick-group">${on.length ? 'Everyone else' : 'Everyone'}, least busy first</div>${rest.map((r) => row('person', r.id, people[r.id].name, avatar(r.id), r.st, '')).join('')}`;
    return html || `<div class="sch-pick-group">${q ? 'Nobody matches' : 'Nobody on the schedule'}</div>`;
  }

  placePicker() {
    if (!this.pick || !this.pickAnchor || !document.body.contains(this.pickAnchor)) { this.closeAssign(); return; }
    // Below the trigger, or above it when there is more room there, kept
    // clear of the app's top bar; the list scrolls inside when it is tall
    const pop = this.assignTarget;
    const r = this.pickAnchor.getBoundingClientRect();
    const bar = document.getElementById('drawer'); // the app's top bar
    const topLimit = Math.max(8, bar ? bar.getBoundingClientRect().bottom + 8 : 8);
    const below = window.innerHeight - r.bottom - 14;
    const above = r.top - 6 - topLimit;
    pop.style.maxHeight = '';
    const natural = pop.offsetHeight;
    const goBelow = natural <= below || below >= above;
    const room = Math.max(160, goBelow ? below : above);
    pop.style.maxHeight = `${Math.min(natural, room)}px`;
    const h = pop.offsetHeight;
    const w = pop.offsetWidth;
    pop.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
    pop.style.top = `${goBelow ? r.bottom + 6 : Math.max(topLimit, r.top - h - 6)}px`;
  }

  // (the redraw detaches the clicked button, so the click must not reach the
  // outside-click check)
  pickTab(event) {
    event.stopPropagation();
    this.pick.tab = event.currentTarget.dataset.tab;
    this.pick.query = '';
    this.drawPicker();
    this.placePicker();
  }

  pickScope(event) {
    this.pick.scope = event.currentTarget.value;
    this.drawPicker();
    this.placePicker();
  }

  pickSearch(event) {
    this.pick.query = event.currentTarget.value;
    const list = this.assignTarget.querySelector('.sch-pick-list');
    if (list) list.innerHTML = this.pickList(this.pickIds());
  }

  pickResource(event) {
    const ids = this.pickIds();
    const { kind, id } = event.currentTarget.dataset;
    this.closeAssign();
    this.send(this.data.get('assignUrl'), 'POST', { task_ids: ids, kind, to_id: id });
  }

  togglePush() {
    this.push = !this.push;
    if (this.hasPushSwitchTarget) this.pushSwitchTarget.classList.toggle('on', this.push);
  }

  // ----- pointer: drags and drag-out creation -----

  // Without the update right only your own row is editable: its blocks
  // (time off, holds) and drawing new ones there
  ownRow(el) {
    const row = el.closest('[data-drop]');
    return !!row && row.dataset.drop === `person:${this.userId}`;
  }

  onPointerDown(e) {
    if (!this.model || e.button !== 0) return;
    const el = e.target.closest('.sch-bar[data-task], .sch-bar[data-block], .sch-sum[data-jobdrag]');
    if (!el) {
      const tl = e.target.closest('.sch-res .sch-tl');
      if (!tl) return;
      if (!this.canEdit && !this.ownRow(tl)) return;
      const drop = (tl.closest('.sch-row').dataset.drop || '').split(':');
      if (!drop[1]) return;
      const rect = tl.getBoundingClientRect();
      const dayPx = rect.width / this.model.days;
      const d0 = this.clampDay(Math.floor((e.clientX - rect.left) / dayPx));
      const marker = document.createElement('div');
      marker.className = 'sch-bar creating';
      marker.style.top = '6px';
      tl.appendChild(marker);
      this.create = { kind: drop[0], resId: drop[1], tl, rect, dayPx, d0, s: d0, e: d0, el: marker, pid: e.pointerId };
      tl.setPointerCapture(e.pointerId);
      e.preventDefault();
      this.drawCreate(e);
      return;
    }
    if (el.classList.contains('locked')) return;
    const isBlock = el.hasAttribute('data-block');
    if (!this.canEdit && !(isBlock && this.ownRow(el))) return;
    const isJob = el.hasAttribute('data-jobdrag');
    let s0; let e0; let item;
    if (isJob) {
      const span = el.dataset.span.split(',').map(Number);
      [s0, e0] = span;
      item = { jobId: el.dataset.jobdrag };
    } else if (isBlock) {
      item = this.blocksById[`${el.dataset.kind}:${el.dataset.block}`] || { id: el.dataset.block, kind: el.dataset.kind, label: el.dataset.label };
      s0 = Number(el.dataset.s); e0 = Number(el.dataset.e);
    } else {
      item = this.tasksById[el.dataset.task];
      if (!item) return;
      s0 = item.s; e0 = item.e;
    }
    const tl = el.parentElement;
    const dayPx = tl.getBoundingClientRect().width / this.model.days;
    const mode = !isJob && e.target.classList.contains('hdl') ? 'resize' : 'move';
    const ownRow = el.closest('[data-drop]');
    this.drag = {
      el, tl, item, isBlock, isJob, mode, dayPx, x0: e.clientX, s0, e0, d: 0, ns: s0, ne: e0,
      over: null, ownDrop: ownRow ? ownRow.dataset.drop : null, pid: e.pointerId, lastDelta: null,
      blockEl: isBlock ? el : null
    };
    this.drag.label = document.createElement('div');
    this.drag.label.className = 'sch-drag-label';
    tl.appendChild(this.drag.label);
    el.setPointerCapture(e.pointerId);
    el.classList.add('dragging');
    e.preventDefault();
    this.updateDrag(e);
  }

  clampDay(i) { return Math.min(this.model.days - 1, Math.max(0, i)); }

  onPointerMove(e) {
    if (this.create) this.drawCreate(e);
    else if (this.drag) this.updateDrag(e);
  }

  onPointerUp(e, cancelled) {
    if (this.create) this.endCreate(e, cancelled);
    else if (this.drag) this.endDrag(e, cancelled);
  }

  updateDrag(e) {
    const d = Math.round((e.clientX - this.drag.x0) / this.drag.dayPx);
    const g = this.drag;
    if (g.mode === 'move') { g.ns = g.s0 + d; g.ne = g.e0 + d; } else { g.ns = g.s0; g.ne = Math.max(g.s0, g.e0 + d); }
    g.d = d;
    const l = Math.max(g.ns, 0); const r = Math.min(g.ne + 1, this.model.days);
    g.el.style.left = this.pct(l);
    g.el.style.width = this.pctW(Math.max(r - l, 0.3));
    let name;
    if (g.isJob) name = `${(this.model.jobs[g.item.jobId] || {}).name || 'Job'}, whole job`;
    else if (g.isBlock) name = `${KIND_LABEL[g.item.kind] || 'Block'}: ${g.item.label || ''}`;
    else name = g.item.name;
    const len = g.ne - g.ns + 1;
    g.label.textContent = `${name} · ${this.range(g.ns, g.ne)}${len > 1 ? ` · ${len} days` : ''}`;
    g.label.style.left = this.pct(l);
    g.label.style.top = g.el.style.top;

    // Ghosts: where the other tasks land if you let go here
    const delta = g.mode === 'move' ? d : g.ne - g.e0;
    let affected = [];
    if (g.isJob) affected = this.model.tasks.filter((x) => String(x.job) === String(g.item.jobId) && x.typed && OPEN.indexOf(x.status) >= 0);
    else if (!g.isBlock && this.push) affected = this.laterTasks(g.item);
    if (delta !== g.lastDelta) {
      g.lastDelta = delta;
      this.drawGhosts(delta ? affected.map((x) => { const p = this.shiftTask(x, delta); return { t: x, s: p.s, e: p.e }; }) : []);
    }
    if (affected.length && delta && !g.isJob) g.label.textContent += ` · +${affected.length} later task${affected.length === 1 ? '' : 's'}`;

    // Hand-over target: another section in the People / Equipment grouping
    if (g.mode === 'move' && this.group !== 'job' && !g.isBlock && !g.isJob) {
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const row = under && under.closest('[data-drop]');
      const key = row ? row.dataset.drop : null;
      if (key !== g.over) {
        this.grid.querySelectorAll('.sch-row.drop-over').forEach((x) => x.classList.remove('drop-over'));
        if (row && key !== g.ownDrop) this.grid.querySelectorAll(`[data-drop="${key}"]`).forEach((x) => x.classList.add('drop-over'));
        g.over = key;
      }
    }
  }

  endDrag(e, cancelled) {
    const g = this.drag;
    this.drag = null;
    this.clearGhosts();
    try { g.el.releasePointerCapture(g.pid); } catch (err) { /* already released */ }
    g.label.remove();
    g.el.classList.remove('dragging');
    this.grid.querySelectorAll('.sch-row.drop-over').forEach((x) => x.classList.remove('drop-over'));
    if (cancelled) { this.refresh(); return; }

    if (g.isJob) {
      if (g.d !== 0) this.send(this.data.get('jobUrl').replace('JOB', g.item.jobId), 'PATCH', { delta: g.d });
      else this.refresh();
      return;
    }
    if (g.isBlock) {
      if (g.d !== 0) {
        this.send(this.blockUrl(g.item.kind, g.item.id), 'PATCH', { starts_on: this.iso(g.ns), ends_on: this.iso(g.ne), label: g.item.label || '' });
      } else {
        this.refreshBar(g);
        this.openPop('edit', g.item, e);
      }
      return;
    }
    const payload = {};
    if (g.d !== 0) {
      const ns = g.mode === 'move' ? this.snapStart(g.item, g.ns) : g.ns;
      payload.start_on = this.iso(ns);
      payload.end_on = this.iso(ns + (g.ne - g.ns));
      payload.push = this.push;
    }
    if (g.mode === 'move' && g.over !== null && g.over !== g.ownDrop) {
      const [kind, toId] = g.over.split(':');
      const fromId = (g.ownDrop || ':').split(':')[1];
      if (kind === this.group) {
        payload.kind = kind;
        payload.from_id = fromId || '';
        payload.to_id = toId || '';
      }
    }
    if (!Object.keys(payload).length) { this.refreshBar(g); return; }
    this.send(this.data.get('taskUrl').replace('TASK', g.item.id), 'PATCH', payload);
  }

  // Put a bar back where it was after a drag that changed nothing
  refreshBar(g) {
    const l = Math.max(g.s0, 0); const r = Math.min(g.e0 + 1, this.model.days);
    g.el.style.left = this.pct(l);
    g.el.style.width = this.pctW(Math.max(r - l, 0.3));
  }

  clearGhosts() { this.grid.querySelectorAll('.sch-bar.ghost').forEach((x) => x.remove()); }

  // Each affected task at its would-be dates: beside its own bar where its
  // row is on screen, otherwise on the job rows it belongs to
  drawGhosts(items) {
    this.clearGhosts();
    items.forEach((it) => {
      if (it.e < 0 || it.s >= this.model.days) return;
      const l = Math.max(it.s, 0); const r = Math.min(it.e + 1, this.model.days);
      const hosts = [];
      this.grid.querySelectorAll(`.sch-bar[data-task="${it.t.id}"]`).forEach((b) => {
        if (!b.closest('.sch-row').hidden) hosts.push({ tl: b.parentElement, top: b.style.top });
      });
      if (!hosts.length) {
        this.grid.querySelectorAll(`.sch-job[data-job-id="${it.t.job}"]`).forEach((row) => {
          const parts = (row.dataset.key || '').split(':');
          if (parts[0] === 'un') { if (it.t.users.length) return; }
          else if (parts[0] !== 'job') {
            const list = parts[0] === 'person' ? it.t.users : it.t.equipment;
            if (list.indexOf(Number(parts[1])) < 0) return;
          }
          hosts.push({ tl: row.querySelector('.sch-tl'), top: '6px' });
        });
      }
      hosts.forEach((h) => {
        const ghost = document.createElement('div');
        ghost.className = 'sch-bar ghost';
        ghost.style.left = this.pct(l);
        ghost.style.width = this.pctW(r - l);
        ghost.style.top = h.top || '6px';
        ghost.innerHTML = `<span class="lab">${this.escape(it.t.name)} → ${this.range(it.s, it.e)}</span>`;
        h.tl.appendChild(ghost);
      });
    });
  }

  drawCreate(e) {
    const c = this.create;
    const d = this.clampDay(Math.floor((e.clientX - c.rect.left) / c.dayPx));
    c.s = Math.min(c.d0, d); c.e = Math.max(c.d0, d);
    c.el.style.left = this.pct(c.s);
    c.el.style.width = this.pctW(c.e - c.s + 1);
    c.el.innerHTML = `<span class="lab">${this.range(c.s, c.e)}</span>`;
  }

  endCreate(e, cancelled) {
    const c = this.create;
    this.create = null;
    try { c.tl.releasePointerCapture(c.pid); } catch (err) { /* already released */ }
    c.el.remove();
    if (!cancelled) this.openPop('new', { resKind: c.kind, resId: c.resId, s: c.s, e: c.e }, e);
  }

  // ----- server -----

  send(url, method, body) {
    this.note('Saving…');
    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || !data.ok) { this.note(`<b>Not saved.</b> ${this.escape(data.error || 'Something went wrong.')}`); this.refresh(); return; }
        this.lastUndo = data.undo && data.undo.length ? data.undo : null;
        const parts = (data.changes || []).map((c) => this.escape(c));
        (data.conflicts || []).forEach((c) => parts.push(`<span class="sch-conf">${this.escape(c)}</span>`));
        this.note(parts.length ? `Saved: ${parts.join(' · ')}` : 'All changes saved', !!this.lastUndo);
        this.refresh();
      })
      .catch(() => { this.note('<b>Not saved.</b> The server could not be reached.'); this.refresh(); });
  }

  undo() {
    if (!this.lastUndo) return;
    const items = this.lastUndo;
    this.lastUndo = null;
    this.send(this.data.get('undoUrl'), 'POST', { undo: items });
  }

  // Re-fetch the chips and grid after a change. The scroll box is part of
  // what is swapped in, so its position is kept across the swap.
  // While another window or grouping is loading, the refresh waits for it
  // (so it fetches the new window), and a refresh that comes back after a
  // later move is dropped (so it can't put the old window back).
  refresh() {
    if (this.navigating) { this.refreshAfterNav = true; return; }
    const seq = this.navSeq;
    const box = this.boardTarget.querySelector('.sch-scroller');
    const scroll = box ? { top: box.scrollTop, left: box.scrollLeft } : null;
    fetch(this.data.get('rowsUrl'), { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => r.text())
      .then((html) => {
        if (seq !== this.navSeq) return;
        this.boardTarget.innerHTML = html;
        this.readBoard();
        const fresh = this.boardTarget.querySelector('.sch-scroller');
        if (fresh && scroll) { fresh.scrollTop = scroll.top; fresh.scrollLeft = scroll.left; }
      })
      .catch(() => {});
  }

  note(html, undoable) {
    if (!this.hasNoteTarget) return;
    this.noteTarget.innerHTML = html + (undoable ? ' <button type="button" class="link focus:outline-none" data-action="click->schedule#undo">Undo</button>' : '');
  }

  escape(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ----- blocks popover -----

  openPop(mode, info, e) {
    if (!this.hasPopTarget) return;
    const resKind = mode === 'new' ? info.resKind : (info.user ? 'person' : 'machine');
    const resId = mode === 'new' ? info.resId : (info.user || info.equipment);
    const name = this.resourceName(resKind, resId);
    this.popState = { mode, info, resKind, resId };
    this.popOpenedAt = Date.now();
    this.popTitleTarget.textContent = `${mode === 'new' ? 'Add to ' : 'Edit on '}${name}`;
    const len = info.e - info.s + 1;
    this.popDatesTarget.textContent = `${this.range(info.s, info.e)}${len > 1 ? ` · ${len} days` : ''}`;
    const kinds = resKind === 'person' ? [['timeoff', 'Time off'], ['hold', 'Hold']] : [['down', 'Planned downtime']];
    const current = mode === 'edit' ? info.kind : kinds[0][0];
    this.popKindTarget.innerHTML = kinds.map(([k, label]) => `<label><input type="radio" name="sch-kind" value="${k}"${k === current ? ' checked' : ''}> ${label}</label>`).join('');
    this.popTextTarget.value = mode === 'edit' ? (info.label || '') : '';
    this.popTextTarget.placeholder = resKind === 'person' ? 'Reason, or what the time is held for' : 'Reason';
    this.popDeleteTarget.hidden = mode !== 'edit';
    const pop = this.popTarget;
    pop.hidden = false;
    pop.style.left = `${Math.max(8, Math.min(e.clientX, window.innerWidth - 290))}px`;
    pop.style.top = `${Math.max(8, Math.min(e.clientY + 12, window.innerHeight - 190))}px`;
    this.popTextTarget.focus();
  }

  resourceName(kind, id) {
    const row = this.grid.querySelector(`.sch-res[data-drop="${kind}:${id}"] .sch-rname`);
    return row ? row.textContent.trim() : (kind === 'person' ? 'this person' : 'this machine');
  }

  closePop() {
    if (this.hasPopTarget) this.popTarget.hidden = true;
    this.popState = null;
  }

  saveBlock() {
    const st = this.popState;
    if (!st) return;
    const checked = this.popKindTarget.querySelector('input[name="sch-kind"]:checked');
    const kind = checked ? checked.value : (st.info.kind || 'timeoff');
    const label = this.popTextTarget.value.trim();
    const body = { starts_on: this.iso(st.info.s), ends_on: this.iso(st.info.e), label };
    this.closePop();
    if (st.mode === 'new') {
      body.kind = kind;
      body.resource_id = st.resId;
      this.send(this.data.get('blocksUrl'), 'POST', body);
    } else if (kind !== st.info.kind) {
      // A changed kind is a new record of the other kind in place of this one
      this.send(this.blockUrl(st.info.kind, st.info.id), 'DELETE', {});
      body.kind = kind;
      body.resource_id = st.resId;
      setTimeout(() => this.send(this.data.get('blocksUrl'), 'POST', body), 150);
    } else {
      this.send(this.blockUrl(st.info.kind, st.info.id), 'PATCH', body);
    }
  }

  deleteBlock() {
    const st = this.popState;
    if (!st || st.mode !== 'edit') return;
    this.closePop();
    this.send(this.blockUrl(st.info.kind, st.info.id), 'DELETE', {});
  }

  blockUrl(kind, id) {
    return this.data.get('blockUrl').replace('KIND', kind).replace('ID', id);
  }

  // ----- chips and spotlight -----

  renderChips() {
    if (!this.hasChipsTarget) return;
    const src = this.hasChipsSourceTarget ? this.chipsSourceTarget : null;
    this.chipsTarget.innerHTML = src ? src.innerHTML : '';
    const cases = (this.model && this.model.cases) || {};
    this.chipsTarget.querySelectorAll('.sch-chip').forEach((chip) => {
      const kind = chip.dataset.chip;
      const list = cases[kind] || [];
      if (!list.length) { delete this.spot.active[kind]; chip.remove(); return; }
      const on = !!this.spot.active[kind];
      chip.classList.toggle('on', on);
      if (on) {
        const i = Math.min(this.spot.idx[kind] || 0, list.length - 1);
        chip.title = `${list[i].text}. Click to clear.`;
        if (list.length > 1) {
          const step = document.createElement('span');
          step.className = 'step';
          step.innerHTML = `<button type="button" data-step="-1" title="Previous">‹</button>${i + 1} of ${list.length}<button type="button" data-step="1" title="Next">›</button>`;
          chip.appendChild(step);
        }
      }
    });
  }

  onClick(e) {
    const stepBtn = e.target.closest('button[data-step]');
    const chip = e.target.closest('.sch-chip[data-chip]');
    if (chip && this.hasChipsTarget && this.chipsTarget.contains(chip)) {
      const kind = chip.dataset.chip;
      const n = ((this.model.cases || {})[kind] || []).length;
      if (stepBtn) {
        this.spot.idx[kind] = (((this.spot.idx[kind] || 0) + Number(stepBtn.dataset.step)) + n) % n;
        delete this.spot.active[kind];
        this.spot.active[kind] = true;
      } else if (this.spot.active[kind]) delete this.spot.active[kind];
      else { this.spot.active[kind] = true; this.spot.idx[kind] = this.spot.idx[kind] || 0; }
      this.spot.scroll = true;
      this.renderChips();
      this.applySpotlight();
    }
  }

  activeCases() {
    const out = [];
    const cases = (this.model && this.model.cases) || {};
    Object.keys(this.spot.active).forEach((kind) => (cases[kind] || []).forEach((c, i) => out.push({ kind, i, c })));
    return out;
  }

  // A spotlighted row inside a folded person or machine (Collapse all):
  // open that section for now, without remembering it
  openSection(row) {
    const body = row.closest('[data-res-body]');
    if (!body || !body.hidden) return;
    const res = this.grid.querySelector(`.sch-res[data-key="${body.dataset.resBody}"]`);
    if (res) this.setRowOpen(res, true);
  }

  applySpotlight() {
    const grid = this.grid;
    if (!grid) return;
    grid.querySelectorAll('.spot-hit, .spot-row').forEach((el) => {
      el.classList.remove('spot-hit', 'spot-red', 'spot-amber', 'spot-current', 'spot-row');
    });
    const list = this.activeCases();
    grid.classList.toggle('spot-on', list.length > 0);
    if (!list.length) return;
    const kinds = Object.keys(this.spot.active);
    const focusKind = kinds[kinds.length - 1];
    const focusI = Math.min(this.spot.idx[focusKind] || 0, ((this.model.cases || {})[focusKind] || []).length - 1);
    list.forEach((a) => {
      const sev = SEVERITY[a.kind] || 'amber';
      const current = a.kind === focusKind && a.i === focusI;
      const classes = ['spot-hit', `spot-${sev}`].concat(current ? ['spot-current'] : []);
      (a.c.tasks || []).forEach((id) => {
        grid.querySelectorAll(`.sch-bar[data-task="${id}"]`).forEach((b) => {
          b.classList.add(...classes);
          const row = b.closest('.sch-row');
          row.classList.add('spot-row');
          // open the job row this task sits under so the bar is on screen
          const parent = grid.querySelector(`.sch-job[data-key="${row.dataset.parent}"]`);
          if (parent && !parent.classList.contains('open')) this.setRowOpen(parent, true);
          this.openSection(row);
        });
      });
      if (a.c.job) {
        grid.querySelectorAll(`.sch-job[data-job-id="${a.c.job}"]`).forEach((row) => {
          row.classList.add('spot-row');
          const sum = row.querySelector('.sch-sum'); if (sum) sum.classList.add(...classes);
          // a row in a closed bucket: open the bucket and ring the row's note
          this.openSection(row);
          const body = row.closest('[data-bucket-body]');
          if (body) {
            const stub = row.querySelector('.sch-stub'); if (stub) stub.classList.add(...classes);
            const bucket = grid.querySelector(`.sch-bkt[data-key="${body.dataset.bucketBody}"]`);
            if (bucket && !bucket.classList.contains('open')) this.setRowOpen(bucket, true);
          }
          if (a.kind === 'pastDue') { const m = row.querySelector('.sch-due'); if (m) m.classList.add(...classes); }
          if (a.kind === 'samples') { const m = row.querySelector('.sch-samp'); if (m) m.classList.add(...classes); }
        });
      }
      if (a.c.res) {
        const row = grid.querySelector(`.sch-res[data-drop="${a.c.kind}:${a.c.res}"]`);
        if (row) {
          row.classList.add('spot-row');
          row.querySelectorAll('.sch-load.l2').forEach((cell) => cell.classList.add(...classes));
        }
      }
    });
    if (this.spot.scroll) {
      this.spot.scroll = false;
      const first = grid.querySelector('.spot-current');
      if (first) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  // ----- keyboard, double-click, outside clicks -----

  onKey(e) {
    if (e.key !== 'Escape') return;
    if (this.pick) { this.closeAssign(); return; }
    if (e.target.matches('input, textarea, select')) return;
    if (this.hasPopTarget && !this.popTarget.hidden) { this.closePop(); return; }
    if (Object.keys(this.spot.active).length) { this.spot.active = {}; this.renderChips(); this.applySpotlight(); }
  }

  onDoubleClick(e) {
    const bar = e.target.closest('.sch-bar[data-task]');
    if (!bar) return;
    const link = bar.closest('.sch-row').querySelector('a[data-task-link]');
    if (link) link.click();
  }

  onDocumentClick(e) {
    // the assign picker closes on a click anywhere else (its own triggers stop the click)
    if (this.pick && this.hasAssignTarget && !this.assignTarget.contains(e.target)) this.closeAssign();
    if (!this.hasPopTarget || this.popTarget.hidden) return;
    if (this.popTarget.contains(e.target) || Date.now() - this.popOpenedAt < 300) return;
    this.closePop();
  }
}
